/**
 * The loading yard's exposed weather.
 *
 * Beat 6 gets its tension from rain, machinery and the intervals between flashes,
 * never from score. These are deliberately neutral materials: the director makes
 * events out of them by choosing when to expose, shelter or interrupt Rosalie.
 */

import { connectChain, envelope, filter, gain, lfo, lfoOut, noiseSource } from '../synth';
import type { SoundDefinition } from '../types';

/** Schedule a closed envelope so a rendered event cannot leave a click at its end. */
function pulse(
  shape: GainNode,
  t0: number,
  start: number,
  attack: number,
  hold: number,
  release: number,
  level: number,
): void {
  shape.gain.setValueAtTime(0, t0 + start);
  shape.gain.linearRampToValueAtTime(level, t0 + start + attack);
  shape.gain.setValueAtTime(level, t0 + start + attack + hold);
  shape.gain.linearRampToValueAtTime(0, t0 + start + attack + hold + release);
}

/**
 * Make a loop genuinely meet its next copy.
 *
 * `noiseSource()` crossfades its buffer, but the gain paths still need their own
 * guard. An OfflineAudioContext stops one sample before its nominal endpoint, so
 * reaching zero slightly early prevents a residue at the wrap from becoming a
 * four-second click.
 */
function loopEnvelope(ctx: BaseAudioContext, duration: number, level: number): GainNode {
  const shape = gain(ctx, 0);
  const t0 = ctx.currentTime;
  shape.gain.setValueAtTime(0, t0);
  shape.gain.linearRampToValueAtTime(level, t0 + 0.018);
  shape.gain.setValueAtTime(level, t0 + duration - 0.03);
  shape.gain.linearRampToValueAtTime(0, t0 + duration - 0.012);
  return shape;
}

/**
 * The shared concrete-rain floor for both shelter states.
 *
 * Keeping the source seeds and broad bands identical is intentional: steel is a
 * material change above the same weather, rather than a second unrelated rain
 * recording that would make entering cover feel like changing scenes.
 */
function rainFloor(ctx: BaseAudioContext, duration: number, out: AudioNode): void {
  connectChain(
    noiseSource(ctx, duration, 701),
    filter(ctx, { type: 'highpass', frequency: 480, q: 0.65 }),
    filter(ctx, { type: 'lowpass', frequency: 5800, q: 0.65 }),
    loopEnvelope(ctx, duration, 0.18),
    out,
  );
  connectChain(
    noiseSource(ctx, duration, 709),
    filter(ctx, { type: 'bandpass', frequency: 1950, q: 0.7 }),
    loopEnvelope(ctx, duration, 0.07),
    out,
  );
}

const rainBed: SoundDefinition = {
  id: 'rain-bed',
  label: 'Rain — on concrete',
  description: 'Dense, slightly bright concrete rain with no repeating gesture.',
  category: 'machine',
  duration: 4,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 0.54);
    connectChain(out, ctx.destination);
    rainFloor(ctx, 4, out);
  },
};

const rainOnSteel: SoundDefinition = {
  id: 'rain-on-steel',
  label: 'Rain — on steel',
  description: 'The concrete rain bed under sparse, hard steel-lid drop ticks.',
  category: 'machine',
  duration: 4,
  loop: true,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.5);
    connectChain(out, ctx.destination);
    rainFloor(ctx, 4, out);

    const ticks = gain(ctx, 0);
    // The offsets deliberately resist a countable roof rhythm. They are fixed
    // only because a replayable loop must not acquire a different seam on every
    // render; the broadband floor remains the dominant weather identity.
    const drops: readonly [number, number, number][] = [
      [0.137, 0.038, 719],
      [0.412, 0.029, 727],
      [0.693, 0.044, 733],
      [1.071, 0.032, 739],
      [1.448, 0.051, 743],
      [1.821, 0.036, 751],
      [2.263, 0.043, 757],
      [2.718, 0.03, 761],
      [3.096, 0.05, 769],
      [3.502, 0.034, 773],
    ];
    for (const [offset, level, seed] of drops) {
      const drop = gain(ctx, 0);
      pulse(drop, t0, offset, 0.0015, 0.001, 0.028, level);
      connectChain(
        noiseSource(ctx, 4, seed),
        filter(ctx, { type: 'bandpass', frequency: 3850, q: 1.45 }),
        drop,
        ticks,
      );
    }
    // No drop is allowed in the last few milliseconds: even a quiet tick at the
    // wrap would make shelter announce itself once per loop instead of feeling
    // like continuous rain above the listener.
    ticks.gain.setValueAtTime(1, t0);
    ticks.gain.setValueAtTime(1, t0 + 3.94);
    ticks.gain.linearRampToValueAtTime(0, t0 + 3.97);
    connectChain(ticks, out);
  },
};

/** Build the two thunder distances from one graph so their material cannot drift. */
function thunder(distance: number, duration: number) {
  return (ctx: BaseAudioContext): void => {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.54);
    connectChain(out, ctx.destination);

    const body = gain(ctx, 0);
    pulse(
      body,
      t0,
      0.035 + distance * 0.11,
      0.012 + distance * 0.2,
      0.075 + distance * 0.17,
      1.45 + distance * 1.45,
      0.31,
    );
    connectChain(
      noiseSource(ctx, duration, 811),
      filter(ctx, { type: 'highpass', frequency: 42, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 980 - distance * 610, q: 0.72 }),
      body,
      out,
    );

    const lowRumble = gain(ctx, 0);
    pulse(
      lowRumble,
      t0,
      0.075 + distance * 0.18,
      0.06 + distance * 0.2,
      0.13 + distance * 0.22,
      2.55 + distance * 1.2,
      0.21,
    );
    connectChain(
      noiseSource(ctx, duration, 821),
      filter(ctx, { type: 'bandpass', frequency: 155 - distance * 55, q: 0.8 }),
      lowRumble,
      out,
    );

    const crack = gain(ctx, 0);
    pulse(crack, t0, 0.032, 0.001, 0.004, 0.075, 0.16 * (1 - distance));
    connectChain(
      noiseSource(ctx, duration, 823),
      filter(ctx, { type: 'highpass', frequency: 1550, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 7200 - distance * 4700, q: 0.7 }),
      crack,
      out,
    );

    // Small, non-even tail swells preserve thunder's weather-scale body without
    // becoming a sequence a player can learn as a timing aid for the crossing.
    const tail = gain(ctx, 0);
    const swells: readonly [number, number, number][] = [
      [0.52, 0.052, 0.38],
      [1.34, 0.039, 0.61],
      [2.43, 0.029, 0.43],
    ];
    for (const [offset, level, release] of swells) {
      pulse(tail, t0, offset + distance * 0.16, 0.035, 0.03, release, level);
    }
    connectChain(
      noiseSource(ctx, duration, 827),
      filter(ctx, { type: 'lowpass', frequency: 720 - distance * 420, q: 0.8 }),
      tail,
      out,
    );
  };
}

const thunderNear: SoundDefinition = {
  id: 'thunder-near',
  label: 'Thunder — overhead',
  description: 'A sharp overhead crack with a heavy, uneven low-mid body.',
  category: 'machine',
  duration: 4.5,
  loop: false,
  render: thunder(0, 4.5),
};

const thunderFar: SoundDefinition = {
  id: 'thunder-far',
  label: 'Thunder — horizon',
  description: 'The same thunder one distance further: soft, rolled off and without a crack.',
  category: 'machine',
  duration: 5.5,
  loop: false,
  render: thunder(1, 5.5),
};

const truckIdle: SoundDefinition = {
  id: 'truck-idle',
  label: 'Truck — diesel idle',
  description: 'Slow, lumpy diesel firing with a distant turbo and injector hiss.',
  category: 'machine',
  duration: 4,
  loop: true,
  render(ctx) {
    const out = gain(ctx, 0.56);
    connectChain(out, ctx.destination);

    const engine = loopEnvelope(ctx, 4, 1);
    connectChain(engine, out);

    // 34 and 37 whole firing-rate cycles make a lumpy 8.5–9.25 Hz idle, while
    // still arriving at the identical modulation phase at the loop point. A
    // free-running non-integral LFO here was a known route to a seam click.
    const firingA = gain(ctx, 0.085);
    lfoOut(lfo(ctx, 8.5, 0.029)).connect(firingA.gain);
    const firingB = gain(ctx, 0.052);
    lfoOut(lfo(ctx, 9.25, 0.018)).connect(firingB.gain);

    const fundamental = ctx.createOscillator();
    fundamental.type = 'sine';
    fundamental.frequency.value = 43;
    connectChain(fundamental, firingA, engine);
    fundamental.start(0);

    const combustion = ctx.createOscillator();
    combustion.type = 'triangle';
    combustion.frequency.value = 86;
    connectChain(combustion, firingB, engine);
    combustion.start(0);

    const rattle = gain(ctx, 0.034);
    lfoOut(lfo(ctx, 8.5, 0.012)).connect(rattle.gain);
    connectChain(
      noiseSource(ctx, 4, 839),
      filter(ctx, { type: 'bandpass', frequency: 410, q: 1.05 }),
      rattle,
      engine,
    );

    connectChain(
      noiseSource(ctx, 4, 853),
      filter(ctx, { type: 'highpass', frequency: 2350, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 6900, q: 0.7 }),
      loopEnvelope(ctx, 4, 0.028),
      engine,
    );
  },
};

/**
 * Beat 14's truck, pulling away down the road below the hill: the idle's
 * engine, taken up through one gear and then away. The distance is in the
 * sound itself — the passband closes and the level falls over fourteen
 * seconds — because the one-shot is placed where the truck starts and a
 * positional source cannot follow it (the engine has no moving emitters).
 */
const truckAway: SoundDefinition = {
  id: 'truck-away',
  label: 'Truck — pulling away',
  description: 'A diesel pulling away through one gear and receding to nothing down a road.',
  category: 'machine',
  duration: 14,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0);
    out.gain.setValueAtTime(0, t0);
    out.gain.linearRampToValueAtTime(0.5, t0 + 0.4);
    out.gain.setValueAtTime(0.5, t0 + 3);
    out.gain.exponentialRampToValueAtTime(0.004, t0 + 13.6);
    out.gain.linearRampToValueAtTime(0, t0 + 14);
    const distance = filter(ctx, { type: 'lowpass', frequency: 1800, q: 0.7 });
    distance.frequency.setValueAtTime(1800, t0);
    distance.frequency.exponentialRampToValueAtTime(260, t0 + 13);
    connectChain(out, distance, ctx.destination);

    // The firing rate rises through the first gear, drops at the change, and
    // rises again: two ramps are the whole of "pulling away".
    const firing = gain(ctx, 0.09);
    const rate = lfo(ctx, 9, 0.03);
    rate.frequency.setValueAtTime(9, t0);
    rate.frequency.linearRampToValueAtTime(15, t0 + 4.5);
    rate.frequency.linearRampToValueAtTime(11, t0 + 5.1);
    rate.frequency.linearRampToValueAtTime(16, t0 + 12);
    lfoOut(rate).connect(firing.gain);

    const fundamental = ctx.createOscillator();
    fundamental.type = 'sine';
    fundamental.frequency.setValueAtTime(43, t0);
    fundamental.frequency.linearRampToValueAtTime(72, t0 + 4.5);
    fundamental.frequency.linearRampToValueAtTime(52, t0 + 5.1);
    fundamental.frequency.linearRampToValueAtTime(78, t0 + 12);
    connectChain(fundamental, firing, out);
    fundamental.start(0);

    const combustion = ctx.createOscillator();
    combustion.type = 'triangle';
    combustion.frequency.setValueAtTime(86, t0);
    combustion.frequency.linearRampToValueAtTime(144, t0 + 4.5);
    combustion.frequency.linearRampToValueAtTime(104, t0 + 5.1);
    combustion.frequency.linearRampToValueAtTime(156, t0 + 12);
    connectChain(combustion, gain(ctx, 0.05), out);
    combustion.start(0);

    connectChain(
      noiseSource(ctx, 14, 857),
      filter(ctx, { type: 'bandpass', frequency: 380, q: 0.9 }),
      gain(ctx, 0.05),
      out,
    );
  },
};

const raceClang: SoundDefinition = {
  id: 'race-clang',
  label: 'Stock race — steel panel',
  description: 'One short, inharmonic galvanized-panel contact with no named pitch.',
  category: 'machine',
  duration: 1.2,
  loop: false,
  render(ctx) {
    const t0 = ctx.currentTime;
    const out = gain(ctx, 0.38);
    connectChain(out, ctx.destination);

    const partials: readonly [number, number, number][] = [
      [513, 0.095, 0.21],
      [787, 0.074, 0.17],
      [1171, 0.061, 0.14],
      [1723, 0.048, 0.105],
      [2539, 0.032, 0.072],
    ];
    for (const [frequency, level, release] of partials) {
      const partial = ctx.createOscillator();
      partial.type = 'sine';
      partial.frequency.value = frequency;
      const shape = gain(ctx, 0);
      pulse(shape, t0, 0.018, 0.002, 0.003, release, level);
      connectChain(partial, shape, out);
      partial.start(0);
    }

    const shoulder = gain(ctx, 0);
    pulse(shoulder, t0, 0.011, 0.003, 0.012, 0.08, 0.13);
    connectChain(
      noiseSource(ctx, 1.2, 859),
      filter(ctx, { type: 'bandpass', frequency: 2050, q: 1.1 }),
      shoulder,
      out,
    );
  },
};

const yardFootfall: SoundDefinition = {
  id: 'yard-footfall',
  label: 'Footfall — wet concrete',
  description: 'Rosalie’s familiar concrete trot with a short standing-water splash.',
  category: 'foley',
  duration: 0.5,
  loop: false,
  render(ctx) {
    const out = gain(ctx, 0.46);
    connectChain(out, ctx.destination);

    connectChain(
      noiseSource(ctx, 0.5, 863),
      // The body remains in the concrete footfall's speaker-safe midrange. The
      // splash is deliberately not the whole sound: low playback gain must not
      // erase the impact Rosalie's feet have made legible since beat 1.
      filter(ctx, { type: 'bandpass', frequency: 630, q: 0.82 }),
      gain(ctx, 0.19),
      envelope(ctx, 0.5, 0.004, 0.22),
      out,
    );

    const ring = ctx.createOscillator();
    ring.type = 'triangle';
    ring.frequency.value = 705;
    connectChain(ring, gain(ctx, 0.047), envelope(ctx, 0.5, 0.003, 0.18), out);
    ring.start(0);

    const splash = gain(ctx, 0);
    // Standing water is a front-edge transient, not a longer bright tail pasted
    // onto her step. Keeping it closed by 90 ms lets the concrete body survive
    // at the deliberately low mix level this beat gives every footfall.
    pulse(splash, ctx.currentTime, 0.005, 0.002, 0.014, 0.067, 0.11);
    connectChain(
      noiseSource(ctx, 0.5, 877),
      filter(ctx, { type: 'highpass', frequency: 1450, q: 0.7 }),
      filter(ctx, { type: 'lowpass', frequency: 5200, q: 0.7 }),
      splash,
      out,
    );
  },
};

export const weatherSounds: readonly SoundDefinition[] = [
  rainBed,
  rainOnSteel,
  thunderNear,
  thunderFar,
  truckIdle,
  truckAway,
  raceClang,
  yardFootfall,
];
